<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/30"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center p-4 text-gray-700">
      <DialogPanel class="w-full max-w-md flex-col rounded bg-gray-50 p-5">
        <div class="mb-3 flex w-full justify-between">
          <DialogTitle class="font-serif text-xl md:text-2xl">Actualizar productos masivamente</DialogTitle>
          <button
            class="ml-auto focus:outline-none"
            @click="emit('update:modelValue', false)"
          >
            <XMarkIcon class="size-6" />
          </button>
        </div>
        <DialogDescription class="mb-5 flex flex-col text-base font-light text-gray-700">
          <p>
            Descarga el archivo Excel con todos tus productos actuales y modifica la información que deseas actualizar:
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- id (recomendado): </span>
            <span>ID del producto para identificarlo de manera única.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- name (opcional): </span>
            <span>Nombre del producto. Si no incluyes ID, se usará para encontrar el producto.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- sku (opcional): </span>
            <span>SKU del producto.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- unitary_price_cents (opcional): </span>
            <span>Precio público sugerido (BRUTO).</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- wholesale_price_cents (opcional): </span>
            <span>Precio mayorista (NETO).</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- inventory (opcional): </span>
            <span>Cantidad en inventario del producto.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- weight (opcional): </span>
            <span>Peso aproximado del producto.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- weight_unit (opcional): </span>
            <span>Unidad de peso del producto, puede ser 'grams' o 'kilograms'.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- status (opcional): </span>
            <span>Estado del producto: 'active', 'inactive', 'out_of_stock'.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- priority (opcional): </span>
            <span>Prioridad del producto (número).</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- bar_code (opcional): </span>
            <span>Código de barras del producto.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- pack_units (opcional): </span>
            <span>Cantidad de unidades por paquete.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- collections (opcional): </span>
            <span>Colecciones separadas por comas.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- labels (opcional): </span>
            <span>Etiquetas separadas por comas.</span>
          </p>
          <p class="ml-2 text-sm">
            <span class="font-normal">- categories (opcional): </span>
            <span>Categorías separadas por comas.</span>
          </p>
          <p class="ml-2 text-sm font-normal">*No modifiques los títulos del archivo excel.</p>
          <p class="ml-2 text-sm font-normal">**Solo edita los valores de los campos que deseas actualizar.</p>
          <p class="mb-2 ml-2 text-sm font-normal">***Mantén el ID para identificar cada producto correctamente.</p>
          <downloadable-files-form
            :action="`/products/download_products_update_template?maker_id=${maker.id}`"
            label="Descargar Productos"
            class="mr-auto rounded-md border border-lk-green px-2 py-1 text-sm font-normal text-lk-green hover:bg-lk-green hover:text-white"
          />
        </DialogDescription>
        <file-uploader
          :max-number-of-files="1"
          :file-type="['.xlsx']"
          @update-file-paths="(value: Record<string, string>) => (filesPath = value)"
        />
        <lokal-loading
          v-if="isLoading"
          class="self-center"
          :loading="isLoading"
        />
        <lokal-button
          v-else
          action-name="Actualizar Productos"
          size="normal"
          :action="`primary`"
          :disabled="Object.keys(filesPath).length === 0"
          @click="updateProducts"
        />
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';
import { ref, computed } from 'vue';
import { useMutation } from 'vue-query';
import productsApi from 'api/products';
import fileUploader from './file-uploader.vue';
import downloadableFilesForm from './downloadable-files-form.vue';
import lokalLoading from './lokal-loading.vue';
import lokalButton from './lokal-button.vue';

interface UpdateProductsDialogProp {
  modelValue: boolean;
  maker: Maker;
}
const props = withDefaults(defineProps<UpdateProductsDialogProp>(), {
  modelValue: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'add-message': [value: string[]];
}>();

const filesPath = ref({} as { [key: string]: string });

const updateProductsMutation = useMutation(
  () => {
    const fileKey = Object.keys(filesPath.value)[0];
    return productsApi.updateProductsFromFile(filesPath.value[fileKey], props.maker.id);
  },
  {
    onSuccess: () => {
      emit('add-message', ['notice', 'Productos actualizados correctamente']);
      emit('add-message', ['notice', 'Debes esperar unos minutos y luego recargar la página para ver los cambios']);
      emit('update:modelValue', false);
    },
  },
);

const isLoading = computed(() => updateProductsMutation.isLoading.value);

function updateProducts() {
  updateProductsMutation.mutate();
}
</script>
